import React, { useEffect, useState, useMemo } from 'react';
import { X, CheckCircle2, EyeOff, Layers, Building2, Target, Server, Shield, Lock, FileText, Briefcase } from 'lucide-react';
import { getBesoinById, type BesoinData } from '../services/besoinsService';
import { getAllClients, type ClientItem } from '../services/clientService';
import { QUESTIONS } from './PriseDeBesoinWizardModal';
import { METIER_FORMS } from '../domain/metier/metierTypes';
import { getIntakeTemplate } from '../domain/besoins/templates';

interface BesoinRapportCompletModalProps {
  isOpen: boolean;
  onClose: () => void;
  besoinId: number;
}

type QuestionDef = any;

// Mapping des icônes par nom
const iconMap: Record<string, any> = {
  Building2,
  Target,
  Server,
  FileText,
  Briefcase,
  Shield,
  Lock,
};

function getPhaseData(besoin: BesoinData, phaseNum: number) {
  const phaseKey = `phase${phaseNum}_data` as keyof BesoinData;
  const data = besoin[phaseKey];
  return (data as Record<string, any>) || {};
}

export default function BesoinRapportCompletModal({
  isOpen,
  onClose,
  besoinId,
}: BesoinRapportCompletModalProps) {
  const [besoin, setBesoin] = useState<BesoinData | null>(null);
  const [client, setClient] = useState<ClientItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Charger le template basé sur le template_id du besoin
  const template = useMemo(() => {
    if (!besoin?.template_id) return null;
    try {
      return getIntakeTemplate(besoin.template_id);
    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      return null;
    }
  }, [besoin?.template_id]);

  useEffect(() => {
    if (!isOpen || !besoinId) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const b = await getBesoinById(besoinId);
        setBesoin(b);
        if (b?.client_id) {
          const clients = await getAllClients();
          const c = clients.find((cl) => cl.id === b.client_id) || null;
          setClient(c);
        }
      } catch (e) {
        console.error('Erreur chargement besoin (rapport complet):', e);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isOpen, besoinId]);

  if (!isOpen) return null;

  if (isLoading || !besoin) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <p className="text-sm text-gray-600">Chargement du rapport complet...</p>
        </div>
      </div>
    );
  }

  const completion = besoin.metadata?.completion_percentage || 0;

  const renderQuestion = (phaseData: any, q: QuestionDef) => {
    const rawValue = phaseData[q.id];

    // Gérer les types de questions du template (label au lieu de question)
    const questionType = q.type || 'text';

    if (questionType === 'multiselect') {
      const selected: string[] = Array.isArray(rawValue)
        ? rawValue
        : rawValue
        ? [rawValue]
        : [];

      return (
        <div className="space-y-2">
          <p className="text-[11px] text-gray-600">
            Sélection(s):{' '}
            {selected.length > 0 ? (
              <span className="font-semibold text-gray-900">{selected.join(', ')}</span>
            ) : (
              <span className="italic text-gray-400">Aucune sélection pour le moment</span>
            )}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {q.options?.map((opt: string) => {
              const isSelected = selected.includes(opt);
              return (
                <span
                  key={opt}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${
                    isSelected
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-gray-50 text-gray-500 border-gray-200'
                  }`}
                >
                  {isSelected ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <EyeOff className="w-3 h-3" />
                  )}
                  {opt}
                </span>
              );
            })}
          </div>
        </div>
      );
    }

    // Pour les champs texte / textarea / select, on affiche simplement la valeur
    const display =
      rawValue === null || rawValue === undefined || rawValue === ''
        ? 'Non renseigné'
        : Array.isArray(rawValue)
        ? rawValue.join(', ')
        : String(rawValue);

    return (
      <p className="text-[12px] text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
        {display}
      </p>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-6">
      <div className="w-[90vw] max-w-[1500px] max-h-[95vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-purple-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shadow-inner">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">
                Rapport complet – Vue analyste
              </p>
              <h2 className="text-lg font-bold">
                {besoin.nom || 'Prise de besoin'} • {client?.nom || `Client #${besoin.client_id}`}
              </h2>
              <p className="text-[11px] text-white/80 mt-0.5">
                Permet de voir tous les choix possibles, avec ce qui a été sélectionné (✅) ou non (œil barré).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="text-white/80 text-[11px]">Complétion</span>
              <div className="w-32 h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-emerald-400 rounded-full"
                  style={{ width: `${completion}%` }}
                />
              </div>
              <span className="text-[11px] font-semibold">{completion}%</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-gradient-to-br from-gray-50 to-blue-50/40 space-y-4">
          {template ? (
            // Utiliser le template chargé dynamiquement
            template.phases.map((phase, phaseIndex) => {
              const phaseNum = phaseIndex + 1;
              const phaseData = getPhaseData(besoin, phaseNum);
              const Icon = phase.icon ? (iconMap[phase.icon] || Building2) : Building2;

              return (
                <div
                  key={phase.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-bz-blue/10 flex items-center justify-center text-bz-blue">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-bz-blue uppercase">
                        PHASE {phaseNum}
                      </p>
                      <h3 className="text-sm font-bold text-gray-900">{phase.title}</h3>
                      {phase.description && (
                        <p className="text-[10px] text-gray-600 mt-0.5">{phase.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-dashed border-gray-200 pt-3 space-y-3">
                    {phase.questions.map((q) => (
                      <div key={q.id} className="space-y-1.5">
                        <p className="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-bz-blue" />
                          {(q as any).label || (q as any).question || q.id}
                        </p>
                        {renderQuestion(phaseData, q)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            // Fallback sur QUESTIONS pour compatibilité avec pdti-standard-pme
            (['phase1', 'phase2', 'phase3', 'phase4'] as const).map((phaseKey) => {
              const def = (QUESTIONS as any)[phaseKey];
              if (!def) return null;
              
              const { title, icon: Icon, questions } = {
                title: def.title as string,
                icon: def.icon,
                questions: def.questions as QuestionDef[],
              };
              const phaseNum = parseInt(phaseKey.replace('phase', ''));
              const phaseData = getPhaseData(besoin, phaseNum);

              return (
                <div
                  key={phaseKey}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-bz-blue/10 flex items-center justify-center text-bz-blue">
                      {phaseKey === 'phase1' ? (
                        <Building2 className="w-4 h-4" />
                      ) : phaseKey === 'phase2' ? (
                        <Target className="w-4 h-4" />
                      ) : phaseKey === 'phase3' ? (
                        <Server className="w-4 h-4" />
                      ) : (
                        <Icon className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-bz-blue uppercase">
                        {phaseKey.toUpperCase()}
                      </p>
                      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
                    </div>
                  </div>

                  <div className="border-t border-dashed border-gray-200 pt-3 space-y-3">
                    {questions.map((q) => (
                      <div key={q.id} className="space-y-1.5">
                        <p className="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-bz-blue" />
                          {q.question}
                        </p>
                        {renderQuestion(phaseData, q)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {/* Phase 5 – Page métier (seulement pour pdti-standard-pme) */}
          {besoin.metier_id && besoin.template_id === 'pdti-standard-pme' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-bz-blue/10 flex items-center justify-center text-bz-blue">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-bz-blue uppercase">PHASE 5</p>
                  <h3 className="text-sm font-bold text-gray-900">
                    Page métier –{' '}
                    {METIER_FORMS.find((f) => f.metierId === besoin.metier_id)?.titre || 'Métier'}
                  </h3>
                </div>
              </div>

              <div className="border-t border-dashed border-gray-200 pt-3 space-y-3">
                {(() => {
                  const form = METIER_FORMS.find((f) => f.metierId === besoin.metier_id);
                  const data = besoin.phase5_metier_data || {};

                  if (!form) {
                    return (
                      <p className="text-[11px] text-gray-500 italic">
                        Aucune configuration détaillée trouvée pour ce métier. Affichage brut des données:
                      </p>
                    );
                  }

                  return form.fields.map((field) => {
                    const rawValue = (data as any)[field.id];

                    let content: React.ReactNode;
                    if (field.type === 'multiselect') {
                      const selected: string[] = Array.isArray(rawValue)
                        ? rawValue
                        : rawValue
                        ? [rawValue]
                        : [];

                      content = (
                        <div className="space-y-2">
                          <p className="text-[11px] text-gray-600">
                            Sélection(s):{' '}
                            {selected.length > 0 ? (
                              <span className="font-semibold text-gray-900">
                                {selected.join(', ')}
                              </span>
                            ) : (
                              <span className="italic text-gray-400">
                                Aucune sélection pour le moment
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {field.options?.map((opt) => {
                              const isSelected = selected.includes(opt);
                              return (
                                <span
                                  key={opt}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${
                                    isSelected
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      : 'bg-gray-50 text-gray-500 border-gray-200'
                                  }`}
                                >
                                  {isSelected ? (
                                    <CheckCircle2 className="w-3 h-3" />
                                  ) : (
                                    <EyeOff className="w-3 h-3" />
                                  )}
                                  {opt}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    } else {
                      const display =
                        rawValue === null || rawValue === undefined || rawValue === ''
                          ? 'Non renseigné'
                          : Array.isArray(rawValue)
                          ? rawValue.join(', ')
                          : String(rawValue);

                      content = (
                        <p className="text-[12px] text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          {display}
                        </p>
                      );
                    }

                    return (
                      <div key={field.id} className="space-y-1.5">
                        <p className="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-bz-blue" />
                          {field.label}
                        </p>
                        {content}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          <div className="text-[11px] text-gray-500 mt-2">
            <p className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="w-3 h-3" />
                Sélectionné
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200">
                <EyeOff className="w-3 h-3" />
                Option non sélectionnée (mais disponible dans le template)
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


